import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../supabase/supabase';

const { FiX, FiAlertTriangle, FiTrash2, FiLock } = FiIcons;

function DeleteAccountModal({ isOpen, onClose }) {
  const { user, signOut } = useAuth();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';

  const handleClose = () => {
    setStep(1);
    setPassword('');
    setConfirmText('');
    setError('');
    onClose();
  };

  const handleVerifyPassword = async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: user.email,
        password,
      });
      if (error) throw new Error('Incorrect password. Please try again.');
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== CONFIRM_PHRASE) {
      setError(`Please type "${CONFIRM_PHRASE}" exactly`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error } = await supabase.rpc('delete_user_account');
      if (error) throw error;
      await signOut();
    } catch (err) {
      console.error('Delete account error:', err);
      setError('Failed to delete account. Please contact support.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-xl max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiTrash2} className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-xl font-space-grotesk font-bold text-secondary">
                  Delete Account
                </h2>
              </div>
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            {/* Warning Banner */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800 mb-1">This action is permanent and irreversible</p>
                  <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
                    <li>All your profile data will be deleted</li>
                    <li>Your public resume will be removed</li>
                    <li>All schools, milestones & deadlines will be lost</li>
                    <li>Guardian relationships will be removed</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center space-x-2 mb-6">
              {[1, 2].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    step >= s ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {s}
                  </div>
                  {s < 2 && <div className={`w-8 h-0.5 mx-1 ${step > s ? 'bg-red-600' : 'bg-gray-200'}`} />}
                </div>
              ))}
              <span className="text-xs text-gray-500 ml-2">
                {step === 1 ? 'Verify password' : 'Confirm deletion'}
              </span>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your password to continue
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleVerifyPassword()}
                      placeholder="Your current password"
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
                    />
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button onClick={handleClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                    Cancel
                  </button>
                  <button
                    onClick={handleVerifyPassword}
                    disabled={loading || !password}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold disabled:opacity-50"
                  >
                    {loading ? 'Verifying...' : 'Continue'}
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <span className="font-mono font-bold text-red-600">{CONFIRM_PHRASE}</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={CONFIRM_PHRASE}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 font-mono"
                  />
                </div>
                <div className="flex space-x-3">
                  <button onClick={handleClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={loading || confirmText !== CONFIRM_PHRASE}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold disabled:opacity-50 flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    <span>{loading ? 'Deleting...' : 'Delete Account'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default DeleteAccountModal;